// The landing page's measurement ids, in ONE place (#2512).
//
// Founder, 2026-09-29: "Always keep an eye on the landing page... based on
// user behavior and the things they click, find out what they are most
// interested in and what would get them onboard." The Desk's landing watch
// (scripts/desk/landing_collect.py, cloud_functions/marketing_agent/
// desk_landing.py) reads these events, so every id here is a contract:
//
//   landing_section_seen   {section, order}   a section reached the middle of
//                                             the screen, once per page view
//   landing_cta_clicked    {cta_id, section}  a link or button with a stable
//                                             id; NEVER its text (autocapture
//                                             text leaked student data, #2511)
//   landing_build_tile_clicked {tool}         a tap on a Build step's tile
//   signin_started         {..., entry}       which CTA or surface started it
//
// Ids are named by MEANING, not by the DOM (`free_read`, not `try-hero`), so
// a landing redesign (mocks in flight, 2026-09-29) keeps them: a moved band
// keeps its id, a renamed button keeps its id, and the before/after windows
// the Desk compares stay comparable. A new section or CTA gets a new id HERE;
// an id is never reused for a different thing.
//
// Python twin: cloud_functions/marketing_agent/desk_landing.py (SECTIONS,
// CTAS, BUILD_TOOLS, SIGNIN_ENTRIES), pinned both ways by
// tests/cloud_functions/marketing_agent/test_desk_landing.py.
//
// Keep this module import-free: the drift tests read it from the source tree.

// Every section id the page carries. `order` on the event is NOT an index
// here: it is the section's position on the page as rendered (the hook counts
// the [data-landing-section] elements top to bottom), so a redesign that
// moves a band changes its order and keeps its id.
//
// Founder pick A (#2513, 2026-09-29, ADR 0022) rebuilt the page. The ids of
// what it removed are RETIRED, never to be reused for something else, so a
// Desk window that spans the change never reads an old band's traffic as a
// new one's: flagship, the_read, mission, trust, stats, astra, profile,
// year_plan, majors, build_proof, activities, visits, list, summers,
// outcomes, essays, roadmap, sharing, agents, resources, film, parents,
// compare, schools; and the CTAs hero_how_it_works, hero_counselor,
// mission_team, trust_founder, trust_patents, trust_principles, trust_policy,
// profile_learn_more, build_paper, build_season_pass, resources_paper,
// film_play_button, film_play_tile, compare_get_started, compare_for_schools,
// schools_caseload, schools_talk, schools_more. The same evening the founder
// moved every quote into the hero's carousel ("why do we have testimonials in
// 2 places"), retiring the section testimonials and testimonials_start_free:
// the quotes are part of `hero` now. Then pick E ("E, with the patent tile
// fixed") replaced the evidence block with the trends band, retiring
// evidence, evidence_source, evidence_see_tools and evidence_start_free, and
// added how, trends, trends_source, pointer and pointer_link. The film's progress events
// (25/50/75/100) went with the film band: /#film now opens the film file
// itself, which no page event can measure.
export const LANDING_SECTIONS = [
    // the page, top to bottom. `hero_chat` (#2725, founder pick B): Astra's
    // open chat leads the page while astra_lite is on, carrying the
    // #see-your-odds anchor; `free_read` then stays off the page.
    // `milestone_ribbon` and `proof_strip` (#3820): shown only after the
    // founder's Go live, the ribbon at the top and the strip under Ask Astra.
    // `colleges` (#4434 plan A): Look up a college, 20 college pages, after How it works.
    // `visitor_maps` (#4607): the band's maps block, inside the proof strip.
    // `masters_abroad` (#4967): India visitors only, behind india_masters.
    'milestone_ribbon', 'hero_chat', 'proof_strip', 'visitor_maps', 'hero', 'build_strip', 'free_read', 'masters_abroad',
    'how', 'colleges', 'trends', 'build', 'planning',
    'planning_data',
    // #4972: Stratia alongside a counselor, after planning.
    'with_counselor',
    'gallery', 'privacy_line', 'counselor_line',
    // #4985: the default region's line to /masters, behind india_masters.
    'masters_link',
    'footer',
    // #5128, the story order (founder pick A of #5093, behind landing_story).
    // Its page order: hero_chat, proof_strip, (masters_abroad), quotes,
    // story, colleges, plan, build, with_counselor, cost, tour_line, then the
    // lines and the footer. colleges, build and with_counselor keep their ids
    // (the same content, moved). When the flag retires with the old order,
    // hero, build_strip, how, trends, planning and gallery are retired here.
    'quotes', 'story', 'plan', 'cost', 'tour_line',
];

// cta_id -> {section, label}. `section` is where the CTA lives ('header' is
// the fixed bar, not a scroll section). `label` is the words on it today,
// when they are fixed text: the Desk quotes it on a card that proposes new
// words, and LandingInstrumentation.test.jsx fails when the page and this
// table disagree. `label: null` for a control whose words are not a fixed
// line (a tool's name, a count, a guide's title).
// A renamed button keeps its id: the hero's and the header's sign-up buttons
// read "Start free" since pick A and stay hero_get_started and
// header_get_started, so their before/after windows compare.
// The free read has no CTA ids of its own: TryHero's try_hero_* events
// measure every step of it, and the Desk reads those. Astra Lite's limit
// card is the exception: its Start free starts a sign-in.
export const LANDING_CTAS = {
    header_resources: { section: 'header', label: 'Resources' },
    header_pricing: { section: 'header', label: 'Pricing' },
    header_counselor: { section: 'header', label: 'For counselors' },
    header_get_started: { section: 'header', label: 'Start free' },
    header_start_free: { section: 'header', label: 'Start free' },
    header_open_app: { section: 'header', label: 'Open App' },
    header_tour: { section: 'header', label: 'Tour' },
    header_news: { section: 'header', label: 'News' },
    header_colleges: { section: 'header', label: 'Colleges' },
    // The proof strip's figures that link to their page, and the ribbon's
    // link and its dismiss (#3820); their words come from the server.
    proof_strip_link: { section: 'proof_strip', label: null },
    milestone_ribbon_link: { section: 'milestone_ribbon', label: null },
    milestone_ribbon_dismiss: { section: 'milestone_ribbon', label: null },
    hero_get_started: { section: 'hero', label: 'Start free' },
    hero_see_tools: { section: 'hero', label: null },
    // The four tiles keep their ids in the story order's Build band (#5128).
    // The Desk files a click by this table (landing_collect groups by
    // cta_id), and the go-live is a flag flip, so ONE of the two orders is
    // misfiled until the flag retires. They stay `build_strip`, true today:
    // after go-live the strip is not reached and the Desk only undercounts
    // them, where `build` now would add top-of-page clicks to the deep Build
    // cards' door rate and could draw a false "move build up" card (review of
    // #5128). The PR that retires the flag files them under `build`. The
    // event itself carries the band the tile sat in (useLandingInstrumentation).
    build_tile_brainstorm: { section: 'build_strip', label: null },
    build_tile_patent: { section: 'build_strip', label: null },
    build_tile_research: { section: 'build_strip', label: null },
    build_tile_pitch: { section: 'build_strip', label: null },
    // The trends band's source links (pick E), and the one pointer.
    trends_source: { section: 'trends', label: null },
    trends_start_free: { section: 'trends', label: 'Start free' },
    build_guide_brainstorm: { section: 'build', label: 'Read the guide' },
    build_guide_patent: { section: 'build', label: 'Read the guide' },
    build_guide_research: { section: 'build', label: 'Read the guide' },
    build_start_free_pitch: { section: 'build', label: 'Start free' },
    build_pricing: { section: 'build', label: 'See what the pass includes' },
    build_start_free: { section: 'build', label: 'Start free' },
    planning_guide: { section: 'planning', label: null },
    planning_resources: { section: 'planning', label: 'Browse them' },
    planning_start_free: { section: 'planning', label: 'Start free' },
    // #4972: Stratia alongside a counselor. The guide link reads the paper's
    // own title, so it has no fixed label here.
    with_counselor_start_free: { section: 'with_counselor', label: 'Start free' },
    with_counselor_guide: { section: 'with_counselor', label: null },
    with_counselor_sharing_guide: { section: 'with_counselor', label: 'How sharing works' },
    // Astra Lite's limit card (#2549): the one Start free inside the free read.
    free_read_astra_limit: { section: 'free_read', label: 'Start free' },
    // Astra's open chat (#2725): the limit card's Start free, and the
    // topic nudge's Start free (one id per nudge, so the Desk reads which
    // topic's promise moves people).
    astra_chat_limit: { section: 'hero_chat', label: 'Start free' },
    // #3480: the unlock box under a locked read (the picture flow runs inside the hero chat)
    astra_read_unlock: { section: 'hero_chat', label: 'Sign up free to see it' },
    astra_chat_nudge_deadlines: { section: 'hero_chat', label: 'Start free' },
    astra_chat_nudge_cost: { section: 'hero_chat', label: 'Start free' },
    astra_chat_nudge_odds: { section: 'hero_chat', label: 'Start free' },
    astra_chat_nudge_majors: { section: 'hero_chat', label: 'Start free' },
    astra_chat_nudge_essays: { section: 'hero_chat', label: 'Start free' },
    astra_chat_nudge_summer: { section: 'hero_chat', label: 'Start free' },
    astra_chat_nudge_record: { section: 'hero_chat', label: 'Start free' },
    // #3600: under the answer to a question for past applicants' reported results
    astra_chat_nudge_results: { section: 'hero_chat', label: 'Start free' },
    // The News story sheet's sign-in line (#3272).
    astra_chat_nudge_news: { section: 'hero_chat', label: 'Start free' },
    // #5229: the nudge's read button after a factual answer about a school
    // (one id across topics; the topic rides on astra_chat_nudge). It opens
    // the free read, not sign-in, so it is no sign-in entry.
    astra_chat_nudge_read: { section: 'hero_chat', label: 'Check it with my grades' },
    // #5237: beside Build my picture on Make it about me; opens sign-in.
    astra_chat_signin_instead: { section: 'hero_chat', label: 'Sign in instead' },
    // The gallery (decision A, 2026-09-29): one id per screen, the guide link
    // under it (named for its guide, so no fixed label), so the Desk can say
    // which screens draw a click; then the tour.
    gallery_patent: { section: 'gallery', label: null },
    gallery_list: { section: 'gallery', label: null },
    gallery_majors: { section: 'gallery', label: null },
    gallery_roadmap: { section: 'gallery', label: null },
    gallery_essays: { section: 'gallery', label: null },
    gallery_outcomes: { section: 'gallery', label: null },
    // Kept by the story order's one line to /tour (#5128), filed there; its
    // label is today's page's words until the flag retires the gallery.
    tour_link: { section: 'tour_line', label: 'See the full tour' },
    // #5128, the story order's bands. The guide links read each guide's own
    // title, so they have no fixed label.
    quotes_start_free: { section: 'quotes', label: 'Start free' },
    story_start_free: { section: 'story', label: 'Start free' },
    plan_guide: { section: 'plan', label: null },
    build_guide: { section: 'build', label: null },
    cost_start_free: { section: 'cost', label: 'Start free' },
    cost_pricing: { section: 'cost', label: 'See pricing' },
    privacy_principles: { section: 'privacy_line', label: 'Our Student Data Principles' },
    counselor_for_schools: { section: 'counselor_line', label: 'See what Stratia does for your caseload' },
    // The US home page's line to /masters (#4985).
    masters_link: { section: 'masters_link', label: 'See how Stratia will help' },
    // The Master's abroad waitlist's submit button (#4967).
    masters_waitlist_join: { section: 'masters_abroad', label: 'Join the waitlist' },
    footer_link: { section: 'footer', label: null },
    // The footer's Connect column (founder, 2026-09-29), one id per profile.
    social_linkedin: { section: 'footer', label: 'LinkedIn' },
    social_facebook: { section: 'footer', label: 'Facebook' },
    social_instagram: { section: 'footer', label: 'Instagram' },
    social_whatsapp: { section: 'footer', label: 'WhatsApp' },
    social_youtube: { section: 'footer', label: 'YouTube' },
    social_tiktok: { section: 'footer', label: 'TikTok' },
};

// The Build steps' keys (content/buildNames.js BUILD_STEP_ORDER, with the
// home first). The event carries the key, never the step's name.
export const BUILD_TOOLS = ['brainstorm', 'patent', 'research', 'pitch'];

// Where a sign-in can start. The landing CTAs that open Google sign-in (every
// Start free on the page passes its own CTA id), then the other surfaces that
// call it. Anything else is recorded as 'other'.
export const SIGNIN_ENTRIES = [
    'header_get_started', 'header_start_free', 'hero_get_started', 'free_read_keep',
    'trends_start_free', 'build_start_free', 'build_start_free_pitch',
    'planning_start_free', 'free_read_astra_limit',
    'with_counselor_start_free',
    // #5128, the story order's Start free buttons.
    'quotes_start_free', 'story_start_free', 'cost_start_free',
    'astra_chat_limit', 'astra_chat_nudge_deadlines', 'astra_chat_nudge_cost', 'astra_chat_nudge_odds', 'astra_chat_nudge_majors', 'astra_chat_nudge_essays', 'astra_chat_nudge_summer', 'astra_chat_nudge_record',
    'astra_chat_nudge_results',
    'astra_chat_nudge_news',
    // #5237: "Sign in instead" beside Build my picture.
    'astra_chat_signin_instead',
    // #3480 (astra_read_unlock): the box under a locked read, and the "What
    // would help my chances" chip that opens it.
    'astra_read_unlock',
    // #5195 (signup_moments, founder pick A): one entry per sign-up moment
    // (content/signupMoments.js MOMENTS); the chances card stops sharing
    // astra_read_unlock with the home box while the flag is on.
    'moment_chances', 'moment_astra_read', 'moment_long_read', 'moment_comparing',
    'pricing', 'claim_credits', 'feedback', 'redirect_retry', 'retry', 'tour',
    'news',
];

export const isLandingSection = (id) => LANDING_SECTIONS.includes(id);
export const isLandingCta = (id) => Object.prototype.hasOwnProperty.call(LANDING_CTAS, id);
export const isBuildTool = (key) => BUILD_TOOLS.includes(key);

/** A sign-in entry as the event records it: a known id, else 'other'.
 *  Callers wire sign-in straight to onClick, so the value can be a click
 *  event; only a known string passes. */
export function signinEntry(value) {
    return typeof value === 'string' && SIGNIN_ENTRIES.includes(value) ? value : 'other';
}
